import React, { useState, useEffect } from "react";
import {
  Plus,
  FileText,
  Download,
  Calendar,
  User,
  Building2,
  Send,
  Edit,
} from "lucide-react";
import { format } from "date-fns";

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import { Skeleton } from "@/components/ui/skeleton";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

import DashboardContainer from "@/components/layout/DashboardContainer";
import ContractorAgreementDashboardForm from "@/components/contractor/ContractorAgreementDashboardForm";
import ApiService from "@/services/api";
import { EndpointKeys } from "@/config/api";
import { useCountry } from "@/contexts/CountryContext";
import {
  ContractorContract,
  ListContractorContractsResponse,
} from "@/types/contractor-agreement";
import { GetInvoicesResponse } from "@/types/invoice";

export default function ContractorContractsContainer() {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const { getDateFormat, currentCountry } = useCountry();
  const [contracts, setContracts] = useState<ContractorContract[]>([]);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [fetchError, setFetchError] = useState<string | null>(null);
  const [showInviteModal, setShowInviteModal] = useState(false);
  const [selectedContract, setSelectedContract] =
    useState<ContractorContract | null>(null);
  const [inviteEmail, setInviteEmail] = useState("");
  const [isSendingInvite, setIsSendingInvite] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [editingContract, setEditingContract] = useState<ContractorContract | null>(null);
  const [contractsWithApprovedItems, setContractsWithApprovedItems] = useState<Set<string>>(new Set());


  // Get country-specific date format
  const getDateDisplayFormat = () => {
    const isUS = currentCountry?.code === 'US';
    return isUS ? "MM/dd/yyyy" : "dd/MM/yyyy";
  };

  useEffect(() => {
    fetchContracts();
  }, []);

  const fetchContracts = async () => {
    try {
      setIsLoading(true);
      setFetchError(null);

      const response =
        await ApiService.request<ListContractorContractsResponse>(
          EndpointKeys.GET_CONTRACTOR_CONTRACTS,
          {
            method: "GET",
          },
        );

      if (response.status === "success") {
        setContracts(response.contracts);
        
        // Check which contracts have approved items
        const contractsWithApproved = new Set<string>();
        
        // Check each contract for approved items in parallel
        const approvedChecks = response.contracts.map(async (contract) => {
          const hasApprovedItems = await checkContractHasApprovedItems(contract.id);
          if (hasApprovedItems) {
            contractsWithApproved.add(contract.id);
          }
        });
        
        await Promise.all(approvedChecks);
        setContractsWithApprovedItems(contractsWithApproved);
      } else {
        throw new Error(response.error || "Failed to fetch Client contracts");
      }
    } catch (error) {
      console.error("Error fetching Client contracts:", error);
      const errorMsg =
        error instanceof Error
          ? error.message
          : "Failed to load Client contracts";
      setFetchError(errorMsg);
      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async (contractorAgreementId: string) => {
    try {
      // Generate download URL for the contractor agreement
      const response = await ApiService.request<{ download_url: string }>(
        EndpointKeys.DOWNLOAD_CONTRACTOR_AGREEMENT,
        {
          method: "GET",
          urlParams: { agreement_id: contractorAgreementId },
        },
      );

      if (response.download_url) {
        // Open the download URL in a new tab
        window.open(response.download_url, "_blank");
      } else {
        throw new Error("No download URL received");
      }
    } catch (error) {
      console.error("Error downloading agreement:", error);
      toast({
        title: "Error",
        description: "Failed to download agreement",
        variant: "destructive",
      });
    }
  };

  const handleSendInvite = (contract: ContractorContract) => {
    setSelectedContract(contract);
    setInviteEmail("");
    setShowInviteModal(true);
  };

  const handleInviteSubmit = async () => {
    if (!selectedContract || !inviteEmail.trim()) {
      toast({
        title: "Error",
        description: "Please enter a valid email address",
        variant: "destructive",
      });
      return;
    }

    try {
      setIsSendingInvite(true);

      await ApiService.request(EndpointKeys.SEND_CLIENT_INVITE, {
        method: "POST",
        urlParams: { contractId: selectedContract.id },
        body: { email: inviteEmail.trim() },
      });

      toast({
        title: "Success",
        description: "Client invitation sent successfully",
      });

      setShowInviteModal(false);
      setInviteEmail("");
      setSelectedContract(null);

      // Refresh contracts to show updated status
      fetchContracts();
    } catch (error) {
      console.error("Error sending client invite:", error);
      toast({
        title: "Error",
        description: "Failed to send client invitation",
        variant: "destructive",
      });
    } finally {
      setIsSendingInvite(false);
    }
  };

  const getStatusBadgeVariant = (status: string) => {
    switch (status) {
      case "ACTIVE":
        return "default";
      case "PENDING_CLIENT_APPROVAL":
        return "secondary";
      case "DRAFT":
        return "outline";
      case "TERMINATED":
      case "CANCELLED":
        return "destructive";
      case "COMPLETED":
        return "default";
      case "SUSPENDED":
        return "secondary";
      default:
        return "outline";
    }
  };

  const formatStatus = (status: string) => {
    return status.replace(/_/g, " ").replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const handleCreateSuccess = () => {
    setShowCreateForm(false);
    fetchContracts(); // Refresh the list
    toast({
      title: "Success",
      description: "Contractor contract created successfully",
    });
  };

  const canSendInvite = (contract: ContractorContract) => {
    return contract.status === "DRAFT" && !contract.clientInvitationSentAt;
  };

  // Check if a contract has any approved invoices or expenses
  const checkContractHasApprovedItems = async (contractId: string): Promise<boolean> => {
    try {
      // Check for approved invoices
      const invoicesResponse = await ApiService.request<GetInvoicesResponse>(
        EndpointKeys.GET_INVOICES,
      );
      
      const hasApprovedInvoices = invoicesResponse.invoices.some(
        invoice => invoice.contractorContractId === contractId && invoice.status === "APPROVED"
      );

      if (hasApprovedInvoices) {
        return true;
      }

      // Check for approved expenses for this contract
      try {
        const expensesResponse = await ApiService.request<{ expenses: any[] }>(
          EndpointKeys.CONTRACTOR_CONTRACT_EXPENSES_APPROVED,
          {
            method: "GET",
            urlParams: { id: contractId },
          }
        );
        
        // If the call succeeds and returns expenses, there are approved expenses
        const hasApprovedExpenses = expensesResponse && 
          Array.isArray(expensesResponse.expenses) && 
          expensesResponse.expenses.length > 0;

        return hasApprovedExpenses;
      } catch (expenseError) {
        // If the expenses API fails (e.g., 404), assume no approved expenses
        console.log(`No approved expenses found for contract ${contractId}`);
        return false;
      }
    } catch (error) {
      console.error("Error checking contract approved items:", error);
      // On error, be conservative and assume there are approved items
      return true;
    }
  };

  const handleEdit = (contract: ContractorContract) => {
    setEditingContract(contract);
    setShowEditForm(true);
  };

  const canEditContract = (contract: ContractorContract) => {
    // Allow editing contracts that don't have approved invoices or expenses
    // regardless of their status (DRAFT, PENDING_CLIENT_APPROVAL, etc.)
    return !contractsWithApprovedItems.has(contract.id);
  };

  const handleEditSuccess = () => {
    setShowEditForm(false);
    setEditingContract(null);
    fetchContracts(); // Refresh the list and re-check approved items
    toast({
      title: "Success",
      description: "Contract updated successfully",
    });
  };

  if (showCreateForm) {
    return (
      <ContractorAgreementDashboardForm
        onSuccess={handleCreateSuccess}
        onCancel={() => setShowCreateForm(false)}
      />
    );
  }

  if (showEditForm && editingContract) {
    return (
      <ContractorAgreementDashboardForm
        onSuccess={handleEditSuccess}
        onCancel={() => {
          setShowEditForm(false);
          setEditingContract(null);
        }}
        isEditMode={true}
        initialContract={editingContract}
      />
    );
  }

  return (
    <>
      <DashboardContainer
        title="Client contracts"
        headerContent={
          <Button onClick={() => setShowCreateForm(true)} className="gap-2">
            <Plus className="h-4 w-4" />
            Create New Contract
          </Button>
        }
      >
        <div className="space-y-6">
          {fetchError && (
            <Alert variant="destructive">
              <AlertTitle>Error</AlertTitle>
              <AlertDescription>{fetchError}</AlertDescription>
            </Alert>
          )}

          {isLoading ? (
            <div className="space-y-4">
              {[...Array(3)].map((_, i) => (
                <Card key={i}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div className="space-y-2">
                        <Skeleton className="h-5 w-48" />
                        <Skeleton className="h-4 w-32" />
                      </div>
                      <Skeleton className="h-6 w-20" />
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-full" />
                      <Skeleton className="h-4 w-full" />
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : contracts.length === 0 ? (
            <Card>
              <CardContent className="flex flex-col items-center justify-center py-12">
                <FileText className="h-12 w-12 text-muted-foreground mb-4" />
                <h3 className="text-lg font-medium mb-2">
                  No Client contracts
                </h3>
                <p className="text-muted-foreground text-center mb-6">
                  You haven't created any Client contracts yet. Create your
                  first contract to get started.
                </p>
                <Button
                  onClick={() => setShowCreateForm(true)}
                  className="gap-2"
                >
                  <Plus className="h-4 w-4" />
                  Create Your First Contract
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="space-y-4">
              {contracts.map((contract) => (
                <Card key={contract.id}>
                  <CardHeader>
                    <div className="flex justify-between items-start">
                      <div>
                        <CardTitle className="flex items-center gap-2">
                          <FileText className="h-5 w-5" />
                          {
                            contract.contractTerms.client_full_legal_name
                          } ↔{" "}
                          {contract.contractTerms.contractor_full_legal_name}
                        </CardTitle>
                        <CardDescription className="flex items-center gap-2 mt-1">
                          <Calendar className="h-4 w-4" />
                          Created{" "}
                          {format(new Date(contract.createdAt), getDateDisplayFormat())}
                        </CardDescription>
                      </div>
                      <Badge variant={getStatusBadgeVariant(contract.status)}>
                        {formatStatus(contract.status)}
                      </Badge>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-sm font-medium">Client</p>
                          <p className="text-sm text-muted-foreground">
                            {contract.contractTerms.client_full_legal_name}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-muted-foreground" />
                        <div>
                          <p className="text-sm font-medium">Contractor</p>
                          <p className="text-sm text-muted-foreground">
                            {contract.contractTerms.contractor_full_legal_name}
                            {contract.contractTerms
                              .contractor_business_name && (
                              <span className="block text-xs">
                                (
                                {
                                  contract.contractTerms
                                    .contractor_business_name
                                }
                                )
                              </span>
                            )}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <div>
                          <p className="text-sm font-medium">Fee Rate</p>
                          <p className="text-sm text-muted-foreground">
                            {contract.contractTerms.fee_currency_symbol}
                            {contract.contractTerms.fee_rate} per{" "}
                            {contract.contractTerms.fee_basis}
                          </p>
                        </div>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <div className="text-sm text-muted-foreground">
                        Start Date:{" "}
                        {contract.contractTerms.start_date
                          ? format(
                              new Date(
                                contract.contractTerms.start_date + "T00:00:00",
                              ),
                              getDateDisplayFormat(),
                            )
                          : "Not specified"}
                      </div>
                      <div className="flex gap-2">
                        {canEditContract(contract) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleEdit(contract)}
                            className="gap-2"
                          >
                            <Edit className="h-4 w-4" />
                            Edit
                          </Button>
                        )}
                        {canSendInvite(contract) && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleSendInvite(contract)}
                            className="gap-2"
                          >
                            <Send className="h-4 w-4" />
                            Send Client Invite
                          </Button>
                        )}
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() =>
                            handleDownload(contract.contractorAgreementId)
                          }
                          className="gap-2"
                        >
                          <Download className="h-4 w-4" />
                          Download
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </div>
      </DashboardContainer>

      {/* Client Invite Modal */}
      <Dialog open={showInviteModal} onOpenChange={setShowInviteModal}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Client Invitation</DialogTitle>
            <DialogDescription>
              Send an invitation to the client to review and approve this
              contract. They will receive an email with a link to create their
              account and access the contract.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="invite-email">Client Email Address</Label>
              <Input
                id="invite-email"
                type="email"
                placeholder="client@example.com"
                value={inviteEmail}
                onChange={(e) => setInviteEmail(e.target.value)}
                disabled={isSendingInvite}
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowInviteModal(false)}
              disabled={isSendingInvite}
            >
              Cancel
            </Button>
            <Button
              onClick={handleInviteSubmit}
              disabled={isSendingInvite || !inviteEmail.trim()}
            >
              {isSendingInvite ? "Sending..." : "Send Invitation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
